import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Pencil } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { lt, enUS } from "date-fns/locale";
import UserAvatar from "components/user-avatar/user-avatar";
import AutoGrowTextarea from "components/comment/auto-grow-textarea";
import { Comment } from "types/Comment";

interface CommentListProps {
  comments: Comment[];
  targetComment: Comment | null;
  newComment: string;
  isLoading: string | undefined;
  onEdit: (comment: Comment | null) => void;
  onSave: (commentId: string) => void;
  onCancel: () => void;
  onNewCommentChange: (value: string) => void;
}

const TEXTAREA_CLASS =
  "block w-full resize-none overflow-y-auto rounded-lg border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-3 py-2 text-base md:text-sm leading-normal text-[var(--p-color-text)] placeholder:text-[var(--p-color-text-secondary)] focus:border-[var(--p-color-border-focus)] focus:outline-none focus:ring-1 focus:ring-[var(--p-color-border-focus)] min-h-[92px] max-h-[180px] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-black/20";

/** Localized "5 minutes ago" / "prieš 5 minutes" — matches the posts module
 *  (date-fns formatDistanceToNow with the lt/en locale from i18n.language). */
function relativeTime(dateString: string, lang: string): string {
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return formatDistanceToNow(d, {
      addSuffix: true,
      locale: lang === "lt" ? lt : enUS,
    });
  } catch {
    return d.toLocaleDateString();
  }
}

/** Full local timestamp for the hover title. */
function absolute(dateString: string): string {
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(
    d.getHours()
  )}:${p(d.getMinutes())}`;
}

const CommentList: React.FC<CommentListProps> = ({
  comments,
  targetComment,
  newComment,
  isLoading,
  onEdit,
  onSave,
  onCancel,
  onNewCommentChange,
}) => {
  const { t, i18n } = useTranslation();
  const { companyId = "" } = useParams<{ companyId: string }>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { session } = useSelector((state: any) => state.authReducer);
  const lang =
    (i18n as unknown as { language?: string }).language || "en";

  if (!comments || comments.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-[var(--p-color-text-secondary)]">
        {isLoading === "fetch"
          ? t("loading", { defaultValue: "Loading…" })
          : t("no_comments_yet", { defaultValue: "No comments yet." })}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {comments.map((comment) => {
        const isAuthor = session?.id === comment.user.id;
        const isEdited = comment.createdAt !== comment.updatedAt;
        const isEditing = targetComment?.id === comment.id;

        return (
          <div key={comment.id} className="flex gap-3">
            <div className="shrink-0 pt-0.5">
              <UserAvatar
                companyId={companyId}
                user={{ id: comment.user.id, name: comment.user.name }}
                size={36}
                rounded="full"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[var(--p-color-text)]">
                  {comment.user.name}
                </span>
                <span
                  className="text-xs text-[var(--p-color-text-secondary)]"
                  title={absolute(comment.createdAt)}
                >
                  {relativeTime(comment.createdAt, lang)}
                </span>
                {isEdited && (
                  <span
                    className="text-xs text-[var(--p-color-text-secondary)]"
                    title={`${t("last_edit", { defaultValue: "Last edit" })}: ${absolute(
                      comment.updatedAt
                    )}`}
                  >
                    ({t("edited", { defaultValue: "edited" })})
                  </span>
                )}
                {isAuthor && !isEditing && (
                  <button
                    type="button"
                    onClick={() => onEdit(comment)}
                    aria-label={t("edit", { defaultValue: "Edit" })}
                    className="ml-auto shrink-0 rounded p-1 max-md:-my-2.5 max-md:p-3 text-[var(--p-color-text-secondary)] hover:bg-[var(--p-color-bg-surface-secondary)] hover:text-[var(--p-color-text)]"
                  >
                    <Pencil size={15} strokeWidth={1.75} />
                  </button>
                )}
              </div>

              {isEditing ? (
                <div className="mt-1.5">
                  <AutoGrowTextarea
                    value={newComment}
                    onChange={(e) => onNewCommentChange(e.target.value)}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Escape") onCancel();
                      if (
                        (e.metaKey || e.ctrlKey) &&
                        e.key === "Enter" &&
                        newComment.trim()
                      ) {
                        e.preventDefault();
                        onSave(comment.id);
                      }
                    }}
                    className={TEXTAREA_CLASS}
                  />
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onSave(comment.id)}
                      disabled={!newComment.trim() || isLoading === "update"}
                      className="rounded-lg bg-[var(--p-color-bg-fill-brand)] px-3 py-1.5 text-sm font-semibold max-md:min-h-11 text-[var(--p-color-text-brand-on-bg-fill)] hover:bg-[var(--p-color-bg-fill-brand-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isLoading === "update"
                        ? t("saving", { defaultValue: "Saving…" })
                        : t("save", { defaultValue: "Save" })}
                    </button>
                    <button
                      type="button"
                      onClick={onCancel}
                      className="rounded-lg border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-3 py-1.5 text-sm font-medium max-md:min-h-11 text-[var(--p-color-text)] hover:bg-[var(--p-color-bg-surface-secondary)]"
                    >
                      {t("cancel", { defaultValue: "Cancel" })}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mt-0.5 whitespace-pre-wrap break-words text-sm leading-relaxed text-[var(--p-color-text)]">
                  {comment.comment}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CommentList;
